import { ref, readonly, computed } from "vue";
import { useRouter } from "vue-router";
import { useQueryClient } from "@tanstack/vue-query";
import { authService } from "@/services/authService";
import { getStoredReferralCode } from "@/utils/referral";
import { passkeyService } from "@/services/passkeyService";
import { useSdk } from "@/composables/useSdk";
import { useAuthStorage } from "@/composables/useAuthStorage";
import { queryKeys } from "@/queries/queryKeys";
import type { AuthUser } from "@/types";
import { AUTH_USER_REFRESHED_EVENT } from "@/services/authUserProfile";

/**
 * Check if user has a specific badge
 * Handles both string[] and Badge[] (objects with name property)
 * Exported for use in components that need custom badge checks
 */
export function hasBadge(badges: unknown[] | undefined, badgeName: string): boolean {
  if (!badges || !Array.isArray(badges)) {
    return false;
  }
  return badges.some((badge) => {
    if (typeof badge === "string") {
      return badge.toLowerCase() === badgeName.toLowerCase();
    }
    if (badge && typeof badge === "object" && "name" in badge) {
      return (badge as { name: string }).name.toLowerCase() === badgeName.toLowerCase();
    }
    return false;
  });
}

// Reactive auth state (singleton)
// Keep the cached user snapshot across reloads and HMR while the SDK restores session state.
const storedUser = authService.getStoredUser();
const hasAccessToken = Boolean(authService.getTokens()?.accessToken);

const user = ref<AuthUser | null>(storedUser);
const isLoading = ref(false);
const error = ref<string | null>(null);
// Only treat auth as initialized when we already have an active access token.
const isInitialized = ref(hasAccessToken);
const authStorage = useAuthStorage();
let authRefreshListenerInstalled = false;

function handleAuthUserRefreshed(event: Event): void {
  const customEvent = event as CustomEvent<AuthUser>;
  if (!customEvent.detail) {
    return;
  }

  user.value = customEvent.detail;
  error.value = null;
}

function ensureAuthRefreshListener(): void {
  if (authRefreshListenerInstalled || typeof window === "undefined") {
    return;
  }

  window.addEventListener(
    AUTH_USER_REFRESHED_EVENT,
    handleAuthUserRefreshed as EventListener,
  );
  authRefreshListenerInstalled = true;
}

/**
 * Composable for authentication state and actions
 */
export function useAuth() {
  ensureAuthRefreshListener();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { restoreSession, refreshSession } = useSdk();

  // Drop per-user cached queries so a different (or signed-out) user never
  // sees the previous session's saved events, claimed businesses, or profile.
  function clearAuthQueries(): void {
    queryClient.removeQueries({ queryKey: queryKeys.profile.all });
    queryClient.removeQueries({ queryKey: [...queryKeys.business.all, "event-favorites"] });
    queryClient.removeQueries({ queryKey: queryKeys.business.myClaimed() });
  }

  // Computed properties
  const isAuthenticated = computed(() => Boolean(user.value));
  const isAdmin = computed(() => hasBadge(user.value?.badges, "administrator"));

  /**
   * Initialize auth state from storage
   * Called once on app startup
   */
  async function initialize(): Promise<void> {
    // A cached user snapshot is not enough after moving web auth to in-memory bearer tokens.
    // We can only skip bootstrap when an active access token is already present.
    if (isInitialized.value && user.value && authService.getTokens()?.accessToken) {
      return;
    }

    isLoading.value = true;
    error.value = null;

    try {
      let tokens = await restoreSession();
      const storedUser = authService.getStoredUser();
      const hasSessionHint = Boolean(tokens?.refreshToken || storedUser);

      if (!tokens?.accessToken && hasSessionHint) {
        tokens = await refreshSession();
      }

      if (tokens?.accessToken) {
        if (storedUser) {
          // Migration: If stored user is missing displayName or referralCode, fetch fresh data
          // Check for undefined specifically since null is a valid "no referral code" value
          const needsMigration = (!storedUser.displayName && storedUser.name) ||
                                 (storedUser.referralCode === undefined);
          if (needsMigration) {
            console.log("Migrating user data: fetching fresh profile (missing displayName or referralCode)");
            await fetchCurrentUser();
          } else {
            user.value = storedUser;
          }
        } else {
          // Fetch fresh user data if we have tokens but no stored user
          await fetchCurrentUser();
        }
      } else if (storedUser) {
        // The cached user is stale if we could not restore the session on startup.
        authService.clearStoredUser();
        user.value = null;
      }
    } catch (err) {
      console.error("Auth initialization error:", err);
      authService.clearStoredUser();
      user.value = null;
      error.value =
        err instanceof Error ? err.message : "Failed to initialize auth";
    } finally {
      isLoading.value = false;
      isInitialized.value = true;
    }
  }

  /**
   * Send magic link to email
   */
  async function sendMagicLink(
    email: string,
    options?: {
      referralCode?: string | null;
      redirect?: string | null;
      platform?: string;
    },
  ): Promise<void> {
    isLoading.value = true;
    error.value = null;

    try {
      await authService.sendMagicLink(email, options);
    } catch (err) {
      error.value =
        err instanceof Error ? err.message : "Failed to send magic link";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Send auth code to email (alias for sendMagicLink with cleaner name)
   */
  async function sendAuthCode(
    email: string,
    options?: {
      referralCode?: string | null;
      redirect?: string | null;
      platform?: string;
    },
  ): Promise<void> {
    isLoading.value = true;
    error.value = null;

    try {
      await authService.sendAuthCode(email, options);
    } catch (err) {
      error.value =
        err instanceof Error ? err.message : "Failed to send auth code";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Verify the auth code from magic link
   */
  async function verifyCode(email: string, code: string): Promise<void> {
    isLoading.value = true;
    error.value = null;

    try {
      const { user: authUser } = await authService.verifyCode(
        email,
        code,
      );

      // Set user state
      user.value = authUser;

      // Navigate to redirect URL or home
      const redirect = router.currentRoute.value.query.redirect as string;
      // replace (not push) so /login is not left below the feed in history;
      // otherwise the PWA back-trap "exits" to /login instead of the OS home.
      await router.replace(redirect || "/");
    } catch (err) {
      error.value =
        err instanceof Error ? err.message : "Failed to verify code";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Login with passkey (WebAuthn)
   * @param email - Optional email to limit which passkeys are accepted
   */
  async function loginWithPasskey(email?: string): Promise<void> {
    isLoading.value = true;
    error.value = null;

    try {
      const { user: authUser } = await passkeyService.authenticateWithPasskey(email);

      // Set user state
      user.value = authUser;

      // Navigate to redirect URL or home
      const redirect = router.currentRoute.value.query.redirect as string;
      // replace (not push) so /login is not left below the feed in history;
      // otherwise the PWA back-trap "exits" to /login instead of the OS home.
      await router.replace(redirect || "/");
    } catch (err) {
      error.value =
        err instanceof Error ? err.message : "Failed to login with passkey";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Login with OAuth provider (Google, Apple, etc.)
   * @param provider - OAuth provider name (e.g., 'google', 'apple')
   * @param code - Authorization code from OAuth callback
   * @param redirectUri - The redirect URI used in the OAuth flow (defaults to current origin + callback path)
   * @param extraData - Additional data for provider-specific needs (e.g., Apple's id_token and user info)
   */
  async function loginWithOAuth(
    provider: string,
    code: string,
    redirectUri?: string,
    extraData?: { id_token?: string | null; user?: string | null },
  ): Promise<void> {
    isLoading.value = true;
    error.value = null;

    try {
      // Default redirect URI is the callback path on current origin
      const uri = redirectUri || `${window.location.origin}/auth/${provider}/callback`;

      const { user: authUser } = await authService.loginWithOAuth(
        provider,
        code,
        uri,
        extraData,
        getStoredReferralCode(),
      );

      // Set user state
      user.value = authUser;

      // Navigate to redirect URL or home
      const redirect = router.currentRoute.value.query.redirect as string;
      // replace (not push) so /login is not left below the feed in history;
      // otherwise the PWA back-trap "exits" to /login instead of the OS home.
      await router.replace(redirect || "/");
    } catch (err) {
      error.value =
        err instanceof Error ? err.message : "Failed to login with OAuth";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Set a developer token directly (for testing)
   */
  async function setDeveloperToken(token: string): Promise<void> {
    isLoading.value = true;
    error.value = null;

    try {
      // Set token in auth service and apiClient
      await authService.setDeveloperToken(token);

      // Fetch user profile with the new token
      await fetchCurrentUser();

      // Navigate to redirect URL or home
      const redirect = router.currentRoute.value.query.redirect as string;
      // replace (not push) so /login is not left below the feed in history;
      // otherwise the PWA back-trap "exits" to /login instead of the OS home.
      await router.replace(redirect || "/");
    } catch (err) {
      error.value =
        err instanceof Error ? err.message : "Failed to set developer token";
      // Clear the invalid token
      await authService.logout();
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Fetch current user profile
   */
  async function fetchCurrentUser(): Promise<void> {
    try {
      const authUser = await authService.fetchCurrentUser();
      user.value = authUser;
    } catch (err) {
      console.error("Failed to fetch user:", err);
      // If fetch fails, clear auth state
      logout();
      throw err;
    }
  }

  /**
   * Refresh current user profile (alias for fetchCurrentUser but doesn't logout on error)
   */
  async function refreshUser(): Promise<void> {
    try {
      const authUser = await authService.fetchCurrentUser();
      user.value = authUser;
    } catch (err) {
      console.error("Failed to refresh user:", err);
      throw err;
    }
  }

  /**
   * Logout and clear all auth state
   */
  async function logout(): Promise<void> {
    await authService.logout();
    user.value = null;
    error.value = null;
    clearAuthQueries();
    router.push("/login");
  }

  /**
   * Clear current error
   */
  function clearError(): void {
    error.value = null;
  }

  /**
   * Set user data directly (used for read-after-write pattern)
   * Updates both the reactive state and localStorage
   */
  function setUser(userData: AuthUser): void {
    user.value = userData;
    authService.storeUser(userData);
  }

  return {
    // State (readonly)
    user: readonly(user),
    isLoading: readonly(isLoading),
    error: readonly(error),
    isInitialized: readonly(isInitialized),
    isAuthenticated,
    isAdmin,

    // Actions
    initialize,
    sendMagicLink,
    sendAuthCode,
    verifyCode,
    loginWithPasskey,
    loginWithOAuth,
    setDeveloperToken,
    fetchCurrentUser,
    refreshUser,
    setUser,
    logout,
    clearError,
  };
}

// Export type for the composable return value
export type UseAuthReturn = ReturnType<typeof useAuth>;
